/**
 * Timeline utility functions
 * Helpers for time conversion, slot grouping, and stacking opportunities
 */

// Convert time to minutes for comparison
export const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;

  // Check 12-hour format with AM/PM first: the bare 24-hour regex below has
  // no end anchor and would otherwise match the "H:MM" prefix of a string
  // like "2:30 PM" and silently ignore the AM/PM, misreading it as 02:30.
  const match12 = timeStr.match(/(\d{1,2}):(\d{2}):?(\d{2})?\s*(AM|PM)/i);
  if (match12) {
    let hours = parseInt(match12[1]);
    const minutes = parseInt(match12[2]);
    const period = match12[4].toUpperCase();

    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes;
  }

  // Handle 24-hour format (HH:MM or HH:MM:SS)
  const match24 = timeStr.match(/^(\d{1,2}):(\d{2})/);
  if (match24) {
    return parseInt(match24[1]) * 60 + parseInt(match24[2]);
  }

  return 0;
};

// Convert time to 24-hour format
export const convertTo24Hour = (timeStr) => {
  if (!timeStr) return '';

  // Already in 24-hour format (HH:MM)
  if (timeStr.match(/^\d{1,2}:\d{2}$/)) {
    const [hours, minutes] = timeStr.split(':');
    return `${hours.padStart(2, '0')}:${minutes}`;
  }

  // Handle 12-hour format with AM/PM
  const match = timeStr.match(/(\d{1,2}):(\d{2}):?(\d{2})?\s*(AM|PM)/i);
  if (match) {
    let hours = parseInt(match[1]);
    const minutes = match[2];
    const period = match[4].toUpperCase();

    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  }

  return timeStr; // Return original if format not recognized
};

// Check if a slot is adjacent to the preferred time
export const isAdjacentToPreferredTime = (slot, preferredTime, duration) => {
  if (!preferredTime) return false;

  const slotMinutes = timeToMinutes(slot.Time);
  const preferredMinutes = timeToMinutes(convertTo24Hour(preferredTime));
  const slotDuration = parseFloat(slot['Duration (hours)']) * 60;

  // Check if this slot ends right when preferred time starts
  const endsAtPreferred = slotMinutes + slotDuration === preferredMinutes;

  // Check if this slot starts right when preferred game would end
  const startsAfterPreferred =
    slotMinutes === preferredMinutes + duration * 60;

  return endsAtPreferred || startsAfterPreferred;
};

// Normalize date to YYYY-MM-DD format for comparison
export const normalizeDate = (dateStr) => {
  if (!dateStr) return '';

  // Already in YYYY-MM-DD format (e.g., "2025-12-13")
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateStr;
  }

  // Handle M/D/YYYY or MM/DD/YYYY format (e.g., "1/31/2026" or "12/31/2026")
  const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) {
    const month = match[1].padStart(2, '0');
    const day = match[2].padStart(2, '0');
    const year = match[3];
    return `${year}-${month}-${day}`;
  }

  return dateStr; // Return as-is if format not recognized
};

// Check if slot is at preferred time
export const isAtPreferredTime = (slot, selectedRequest) => {
  if (!selectedRequest) return false;

  const slotTime = convertTo24Hour(slot.Time);
  const preferredTime = convertTo24Hour(selectedRequest['Preferred Time']);

  return slotTime === preferredTime;
};

// Group slots by date, venue, and field
export const groupSlotsByField = (
  available,
  selectedRequest,
  selectedDate,
  selectedVenue,
  dates,
  selectedVenues
) => {
  // If a request is selected, use its date and venue
  const dateToFilter = selectedRequest
    ? selectedRequest['Requested Date']
    : selectedDate || dates[0];

  const venueToFilter = selectedRequest
    ? selectedRequest['Requested Venue']
    : selectedVenue;

  // Normalize the date to compare
  const normalizedDateToFilter = normalizeDate(dateToFilter);

  const filtered = available.filter((slot) => {
    if (
      dateToFilter &&
      normalizeDate(slot.Date) !== normalizedDateToFilter
    ) {
      return false;
    }

    // If a request is selected, use the selectedVenues set for multi-venue filtering
    if (selectedRequest) {
      if (selectedVenues && selectedVenues.size > 0) {
        if (!selectedVenues.has(slot.Venue)) return false;
      } else {
        // Fallback: honor the requested venue
        if (slot.Venue !== selectedRequest['Requested Venue']) return false;
      }
    }

    // Otherwise use the venue dropdown filter
    if (!selectedRequest && venueToFilter && venueToFilter !== 'all') {
      if (slot.Venue !== venueToFilter) return false;
    }

    // If a request is selected, filter by field size compatibility
    // Strict matching: each size must match exactly
    if (selectedRequest) {
      const requestedSize = selectedRequest['Field Size'];
      const slotSize = slot['Field Size'];

      if (requestedSize !== slotSize) return false;
    }

    return true;
  });

  // Group by venue and field
  const grouped = {};
  filtered.forEach((slot) => {
    const key = `${slot.Venue}|${slot['Field #']}`;
    if (!grouped[key]) {
      grouped[key] = {
        venue: slot.Venue,
        field: slot['Field #'],
        date: slot.Date,
        slots: [],
      };
    }
    grouped[key].slots.push(slot);
  });

  // Sort slots by time
  Object.values(grouped).forEach((group) => {
    group.slots.sort((a, b) => timeToMinutes(a.Time) - timeToMinutes(b.Time));
  });

  return grouped;
};

// Find stacking opportunities - slots directly before or after booked slots
// (targetDuration is accepted for API symmetry with callers but not currently used)
export const findStackingOpportunities = (fieldGroup, _targetDuration) => {
  const opportunities = [];
  const slots = fieldGroup.slots;

  for (let i = 0; i < slots.length; i++) {
    const current = slots[i];

    // Only consider available slots
    if (current.IsBooked === 'TRUE') {
      continue;
    }

    const currentDuration = parseFloat(current['Duration (hours)']);
    const currentStartMinutes = timeToMinutes(current.Time);
    const currentEndMinutes = currentStartMinutes + currentDuration * 60;

    let foundStack = false;

    // Check if there's a booked slot directly before this one
    if (i > 0) {
      const previous = slots[i - 1];
      if (previous.IsBooked === 'TRUE') {
        const prevDuration = parseFloat(previous['Duration (hours)']);
        const prevStartMinutes = timeToMinutes(previous.Time);
        const prevEndMinutes = prevStartMinutes + prevDuration * 60;

        const gap = Math.abs(currentStartMinutes - prevEndMinutes);

        // If previous slot ends when current slot begins (or within 15 min)
        if (gap <= 15) {
          opportunities.push({ index: i, slots: [current] });
          foundStack = true;
        }
      }
    }

    // Check if there's a booked slot directly after this one
    if (!foundStack && i < slots.length - 1) {
      const next = slots[i + 1];
      if (next.IsBooked === 'TRUE') {
        const nextStartMinutes = timeToMinutes(next.Time);
        const gap = Math.abs(nextStartMinutes - currentEndMinutes);

        // If current slot ends when next slot begins (or within 15 min)
        if (gap <= 15) {
          opportunities.push({ index: i, slots: [current] });
        }
      }
    }
  }

  return opportunities;
};
